import jsonZoomUIStatuses from '../zoom/jsonZoomUIStatuses.json';
import { findMarkerStatus } from '../global/MapUtils';

/** Icon edge length (px) when frontStyleLvz < 2 (custom markers do not render below min LVZ). */
export const CUSTOM_MARKER_ICON_SIZE_SMALL_PX = 20;

/** Google zoom 14–15 (frontStyleLvz 2). First visibility band — keep icons unobtrusive. */
export const CUSTOM_MARKER_ICON_SIZE_COMPACT_PX = 32;

/** Google zoom 16–18 (frontStyleLvz 3). Intermediate legibility without mid-zoom clutter. */
export const CUSTOM_MARKER_ICON_SIZE_MID_PX = 48;

/** Google zoom 19+ (frontStyleLvz ≥ 4). Prioritize legibility at max street zoom. */
export const CUSTOM_MARKER_ICON_SIZE_LARGE_PX = 64;

const liveviewZoneConfig = jsonZoomUIStatuses.liveviewZone;
const LIVEVIEW_ZONE_ZOOM_THRESHOLDS = liveviewZoneConfig.zoom;
const LIVEVIEW_ZONE_ICONS = liveviewZoneConfig.icons;

/**
 * Google Maps zoom (inclusive) at which custom POI markers should first render.
 * Derived from liveviewZone.zoom[2] — start of frontStyleLvz 2 (quartier band).
 *
 * Google zoom ↔ frontStyleLvz (liveviewZone, via findMarkerStatus):
 * | Google zoom | frontStyleLvz |
 * |-------------|---------------|
 * | < 13        | 0             |
 * | 13          | 1             |
 * | 14–15       | 2             |
 * | 16–18       | 3             |
 * | 19–22       | 4             |
 * | ≥ 23        | 4             |
 *
 * Custom marker icon size (px) by frontStyleLvz:
 * | frontStyleLvz | Google zoom | size (px) |
 * |---------------|-------------|-----------|
 * | 2             | 14–15       | 32        |
 * | 3             | 16–18       | 48        |
 * | ≥ 4           | 19+         | 64        |
 */
export const CUSTOM_MARKER_MIN_GOOGLE_ZOOM = LIVEVIEW_ZONE_ZOOM_THRESHOLDS[2];

/**
 * Trolley POIs (Airtable `description` ≈ "Trolley") stay hidden until this zoom.
 * Derived from liveviewZone.zoom[3] — start of frontStyleLvz 3.
 */
export const CUSTOM_MARKER_TROLLEY_MIN_GOOGLE_ZOOM =
    LIVEVIEW_ZONE_ZOOM_THRESHOLDS[3];

export const CUSTOM_MARKER_MIN_FRONT_STYLE_LVZ = findMarkerStatus(
    LIVEVIEW_ZONE_ZOOM_THRESHOLDS,
    LIVEVIEW_ZONE_ICONS,
    CUSTOM_MARKER_MIN_GOOGLE_ZOOM,
);

export function liveviewZoneFrontStyleLvzForGoogleZoom(googleZoom) {
    return findMarkerStatus(
        LIVEVIEW_ZONE_ZOOM_THRESHOLDS,
        LIVEVIEW_ZONE_ICONS,
        googleZoom,
    );
}

export function googleZoomMeetsCustomMarkerMin(googleZoom) {
    if (googleZoom == null || Number.isNaN(googleZoom)) {
        return false;
    }
    return googleZoom >= CUSTOM_MARKER_MIN_GOOGLE_ZOOM;
}

export function isTrolleyCustomMarkerDescription(description) {
    if (description == null || typeof description !== 'string') {
        return false;
    }
    return description.trim().toLowerCase() === 'trolley';
}

export function getCustomMarkerMinGoogleZoom(description) {
    if (isTrolleyCustomMarkerDescription(description)) {
        return CUSTOM_MARKER_TROLLEY_MIN_GOOGLE_ZOOM;
    }
    return CUSTOM_MARKER_MIN_GOOGLE_ZOOM;
}

export function googleZoomMeetsCustomMarkerVisibility(googleZoom, description) {
    if (googleZoom == null || Number.isNaN(googleZoom)) {
        return false;
    }
    return googleZoom >= getCustomMarkerMinGoogleZoom(description);
}

/**
 * Maps liveviewZone frontStyleLvz to custom POI marker icon size (square, px).
 * Aligns with jsonZoomUIStatuses.liveviewZone zoom bands; only used for custom markers.
 */
export function getCustomMarkerIconSizePx(frontStyleLvz) {
    if (frontStyleLvz < 2) {
        return CUSTOM_MARKER_ICON_SIZE_SMALL_PX;
    }
    if (frontStyleLvz === 2) {
        return CUSTOM_MARKER_ICON_SIZE_COMPACT_PX;
    }
    if (frontStyleLvz === 3) {
        return CUSTOM_MARKER_ICON_SIZE_MID_PX;
    }
    return CUSTOM_MARKER_ICON_SIZE_LARGE_PX;
}

/**
 * Bottom-center anchor for a square map icon (tip sits on the lat/lng).
 */
export function getCustomMarkerIconAnchor(sizePx) {
    return { x: sizePx / 2, y: sizePx };
}

export { LIVEVIEW_ZONE_ZOOM_THRESHOLDS };
